import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Grid,
    Stack,
    Typography
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
    Cell,
    Legend,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip
} from "recharts";
import dashboardServices from "app/services/dashboard-services";
import { DASHBOARD_COLORS, metricChipSx, neutralChipSx } from "./chartColors";

const formatQty = (value) => Number(value || 0).toLocaleString("es-CL");
// "conductoresActivos": personas que condujeron hoy, contra las licencias de App Conductor
// (ver hermes-backend Licensing::Usage y Licensing::Seats).
const METRIC_ORDER = ["entregas", "rutas", "usuariosMobile", "conductoresActivos", "mercadoLibre"];
const METRIC_LABELS = {
    entregas: "Entregas",
    rutas: "Rutas",
    usuariosMobile: "App Conductor",
    conductoresActivos: "Conductores del día",
    mercadoLibre: "Cuentas Mercado Libre"
};
const METRIC_USED_COLORS = {
    entregas: DASHBOARD_COLORS.finished,
    rutas: "#7C3AED",
    usuariosMobile: "#0EA5A4",
    conductoresActivos: "#0E7490",
    mercadoLibre: "#F59E0B"
};

const formatDateLabel = (isoDate) => {
    if (!isoDate) return "-";
    const date = new Date(`${isoDate}T00:00:00`);
    return date.toLocaleDateString("es-CL");
};

const UsageTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    return (
        <Box
            sx={{
                p: 1.25,
                borderRadius: 1.5,
                bgcolor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
                boxShadow: "0 8px 24px rgba(0,0,0,0.08)"
            }}
        >
            {payload.map((item) => (
                <Typography key={item.name} variant="body2" sx={{ color: item.color }}>
                    {item.name}: {formatQty(item.value)}
                </Typography>
            ))}
        </Box>
    );
};

const SubscriptionOrderUsage = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [data, setData] = useState(null);

    useEffect(() => {
        let isMounted = true;

        const loadData = async () => {
            setLoading(true);
            setError("");
            try {
                const response = await dashboardServices.getSubscriptionOrderUsage();
                if (isMounted) setData(response);
            } catch (loadError) {
                if (isMounted) {
                    setData(null);
                    setError("No fue posible cargar el uso de límites del plan.");
                }
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        loadData();
        return () => {
            isMounted = false;
        };
    }, []);

    const period = data?.period || {};
    const plan = data?.plan || {};

    const metrics = useMemo(() => {
        const incoming = Array.isArray(data?.metrics)
            ? data.metrics
            : data?.metric
                ? [data.metric]
                : [];

        const byKey = incoming.reduce((acc, row) => {
            if (row?.feature_key) acc[row.feature_key] = row;
            return acc;
        }, {});

        return METRIC_ORDER.map((featureKey) => {
            const metricRow = byKey[featureKey] || {};
            return {
                feature_key: featureKey,
                label: metricRow.label || METRIC_LABELS[featureKey],
                enabled: metricRow.enabled === undefined ? true : Boolean(metricRow.enabled),
                used: Number(metricRow.used || 0),
                limit: metricRow.limit === null || metricRow.limit === undefined ? null : Number(metricRow.limit),
                unlimited: Boolean(metricRow.unlimited),
                remaining: metricRow.remaining === null || metricRow.remaining === undefined ? null : Number(metricRow.remaining),
                over_by: Number(metricRow.over_by || 0),
                usage_percent: metricRow.usage_percent
            };
        });
    }, [data]);

    const buildMetricChartData = (metricRow) => {
        const hasNumericLimit = !metricRow.unlimited && Number(metricRow.limit || 0) > 0;

        if (hasNumericLimit && metricRow.over_by > 0) {
            return [
                { name: "Límite plan", value: Number(metricRow.limit || 0), color: DASHBOARD_COLORS.pendingProgrammed },
                { name: "Sobreconsumo", value: Number(metricRow.over_by || 0), color: "#DC2626" }
            ];
        }

        if (hasNumericLimit) {
            return [
                { name: "Usado", value: Number(metricRow.used || 0), color: METRIC_USED_COLORS[metricRow.feature_key] || DASHBOARD_COLORS.inProgress },
                { name: "Disponible", value: Number(metricRow.remaining || 0), color: "#D1D5DB" }
            ];
        }

        return [{ name: "Usado", value: Number(metricRow.used || 0), color: METRIC_USED_COLORS[metricRow.feature_key] || DASHBOARD_COLORS.inProgress }];
    };

    return (
        <Card sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Stack spacing={2.5}>
                    <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1.5}>
                        <Stack spacing={0.5}>
                            <Typography variant="h4">Uso del plan: entregas, rutas, conductores y Mercado Libre</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Monitorea cuánto llevas utilizado frente a los límites de tu plan actual.
                            </Typography>
                        </Stack>
                        <Button
                            variant="outlined"
                            onClick={() => navigate("/subscriptions")}
                            sx={{ alignSelf: { xs: "flex-start", md: "center" } }}
                        >
                            Ver suscripciones
                        </Button>
                    </Stack>

                    {error && <Alert severity="error">{error}</Alert>}

                    {loading ? (
                        <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 220 }}>
                            <CircularProgress />
                        </Stack>
                    ) : (
                        <Stack spacing={2}>
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                                <Chip sx={neutralChipSx()} label={`Plan: ${plan?.name || "Sin plan"}`} />
                                <Chip sx={neutralChipSx()} label={`Uso del día: ${formatDateLabel(period.from)}`} />
                            </Stack>

                            <Grid container spacing={2}>
                                {metrics.map((metricRow) => {
                                    const hasNumericLimit = !metricRow.unlimited && Number(metricRow.limit || 0) > 0;
                                    const chartData = buildMetricChartData(metricRow);
                                    const chartTotal = chartData.reduce((acc, item) => acc + Number(item.value || 0), 0);
                                    const centerValue = hasNumericLimit && metricRow.usage_percent !== null
                                        ? `${Math.round(Number(metricRow.usage_percent || 0))}%`
                                        : formatQty(metricRow.used);

                                    return (
                                        <Grid item xs={12} md={6} key={metricRow.feature_key}>
                                            <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, p: 1.5, height: "100%" }}>
                                                <Stack spacing={1.25}>
                                                    <Typography variant="h6">{metricRow.label}</Typography>
                                                    <Stack direction="row" spacing={1} flexWrap="wrap">
                                                        <Chip
                                                            sx={metricChipSx(METRIC_USED_COLORS[metricRow.feature_key] || DASHBOARD_COLORS.inProgress)}
                                                            label={`Usado: ${formatQty(metricRow.used)}`}
                                                        />
                                                        {hasNumericLimit ? (
                                                            <Chip
                                                                sx={metricChipSx(DASHBOARD_COLORS.pendingProgrammed)}
                                                                label={`Límite: ${formatQty(metricRow.limit)}`}
                                                            />
                                                        ) : !metricRow.enabled ? (
                                                            <Chip sx={neutralChipSx()} label="No incluido en plan" />
                                                        ) : (
                                                            <Chip sx={neutralChipSx()} label="Límite: no definido" />
                                                        )}
                                                    </Stack>

                                                    <Box sx={{ height: 220, position: "relative" }}>
                                                        {chartTotal > 0 ? (
                                                            <>
                                                                <ResponsiveContainer width="100%" height="100%">
                                                                    <PieChart>
                                                                        <Pie
                                                                            data={chartData}
                                                                            dataKey="value"
                                                                            nameKey="name"
                                                                            innerRadius={46}
                                                                            outerRadius={72}
                                                                            paddingAngle={2}
                                                                        >
                                                                            {chartData.map((entry) => (
                                                                                <Cell key={`${metricRow.feature_key}-${entry.name}`} fill={entry.color} />
                                                                            ))}
                                                                        </Pie>
                                                                        <Tooltip content={<UsageTooltip />} />
                                                                        <Legend />
                                                                    </PieChart>
                                                                </ResponsiveContainer>
                                                                <Box
                                                                    sx={{
                                                                        position: "absolute",
                                                                        inset: 0,
                                                                        display: "flex",
                                                                        flexDirection: "column",
                                                                        alignItems: "center",
                                                                        justifyContent: "center",
                                                                        pointerEvents: "none"
                                                                    }}
                                                                >
                                                                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                                                        {centerValue}
                                                                    </Typography>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {hasNumericLimit ? "del límite" : "utilizado"}
                                                                    </Typography>
                                                                </Box>
                                                            </>
                                                        ) : (
                                                            <Stack alignItems="center" justifyContent="center" sx={{ height: "100%" }}>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Sin uso registrado.
                                                                </Typography>
                                                            </Stack>
                                                        )}
                                                    </Box>
                                                </Stack>
                                            </Box>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </Stack>
                    )}
                </Stack>
            </CardContent>
        </Card>
    );
};

export default SubscriptionOrderUsage;
